import { Container } from '@ivorobioff/shared';
import Operation, { OperationIntent } from '../models/Operation';
import Plan, { PlanAmountsToUpdate, } from '../models/Plan';
import PlanService from './PlanService';
import { calculateAmount, fixedAmount } from '../random/utils';
import { throwMappedAmount } from '../mapping/operators';
import Account from '../models/Account';
import BudgetOperationService from './BudgetOperationService';
import { BudgetService } from './BudgetService';
import { TransferToAccount, TransferToPlan } from '../models/PlanOperation';
import Budget from '../models/Budget';

interface TransferToAccountParams {
  accounts: Account[];
}

export default class PlanOperationService {
  private planService: PlanService;
  private budgetOperationService: BudgetOperationService;
  private budgetService: BudgetService;

  constructor(container: Container) {
    this.planService = container.get(PlanService);
    this.budgetOperationService = container.get(BudgetOperationService);
    this.budgetService = container.get(BudgetService);
  }

  async submitForAvailable(plan: Plan, operation: Operation): Promise<Plan> {
    let availableAmount = calculateAmount(plan.availableAmount, operation);
    let plannedAmount = plan.plannedAmount;
    let version = plan.version;

    if (availableAmount < 0) {
      const missingAmount = Math.abs(availableAmount);
      plannedAmount = fixedAmount(plannedAmount + missingAmount);
      availableAmount = fixedAmount(plan.availableAmount + missingAmount);

      ({ version } = await this.planService.updateAmountsAsync(plan.id, {
        plannedAmount,
        availableAmount,
        note: operation.note,
        accountId: plan.accountId,
        version
      }));

      availableAmount = 0;
    }

    if (availableAmount > plannedAmount) {
      plannedAmount = availableAmount;
    }

    let checkpointAmount = plan.checkpointAmount;

    if (availableAmount > plan.availableAmount) {
      checkpointAmount = plannedAmount;
    }

    const amount: PlanAmountsToUpdate = {
      availableAmount,
      plannedAmount,
      checkpointAmount,
      note: operation.note,
      accountId: plan.accountId,
      version
    };

    try {
      return this.planService.updateAmountsAsync(plan.id, amount);
    } catch (e) {
      throwMappedAmount(e, 'availableAmount');
    }
  }

  validateForPlanned(plan: Plan, operation: Operation): string | undefined {
    const plannedAmount = calculateAmount(plan.plannedAmount, operation);

    if (plannedAmount < 0) {
      return 'Result must be greater than, or equal to 0';
    }

    const diffAmount = fixedAmount(plannedAmount - plan.plannedAmount);

    const availableAmount = fixedAmount(plan.availableAmount + diffAmount);

    return availableAmount < 0 ? 'Available Amount will be less than 0' : undefined;
  }

  async submitForPlanned(plan: Plan, operation: Operation): Promise<{ differenceAmount: number; plan: Plan; }> {
    const plannedAmount = calculateAmount(plan.plannedAmount, operation);
    const checkpointAmount = plannedAmount;

    const differenceAmount = fixedAmount(plannedAmount - plan.plannedAmount);
    const availableAmount = fixedAmount(plan.availableAmount + differenceAmount);

    const amounts: PlanAmountsToUpdate = {
      plannedAmount,
      checkpointAmount,
      availableAmount,
      note: operation.note,
      version: plan.version
    };

    try {
      return {
        plan: await this.planService.updateAmountsAsync(plan.id, amounts),
        differenceAmount
      };
    } catch (e) {
      throwMappedAmount(e, 'plannedAmount');
    }
  }

  async submitForBoth(plan: Plan, operation: Operation): Promise<Plan> {
    const availableAmount = calculateAmount(plan.availableAmount, operation);

    if (availableAmount < 0) {
      throw new Error('Available Amount must be greater or equal to 0');
    }

    const plannedAmount = calculateAmount(plan.plannedAmount, operation);

    if (plannedAmount < 0) {
      throw new Error('Planned Amount must be greater or equal to 0');
    }

    const checkpointAmount = plannedAmount;

    return this.planService.updateAmountsAsync(plan.id, {
      availableAmount,
      plannedAmount,
      checkpointAmount,
      note: operation.note,
      version: plan.version
    });
  }

  async transferToAccount(
    { sourceAccountId, targetAccountId, targetAmount, sourceAmount }: TransferToAccount,
    { accounts }: TransferToAccountParams): Promise<{ sourcePlan: Plan; targetBudget: Budget; }> {
    const sourceAccount = accounts.find(({ id }) => id === sourceAccountId)!;
    const targetAccount = accounts.find(({ id }) => id === targetAccountId)!;

    const toTitle = `to ${targetAccount.name}`;
    const fromTitle = `from ${sourceAccount.name}`;

    const [sourcePlans, budget] = await Promise.all([
      this.planService.getAllAsync(sourceAccountId),
      this.budgetService.getAsync({ accountId: targetAccountId }),
    ]);

    let sourcePlan = sourcePlans.find(({ name }) => name === toTitle);

    if (!sourcePlan) {
      sourcePlan = await this.planService.createAsync({
        name: toTitle,
        accountId: sourceAccountId,
        plannedAmount: 0,
        availableAmount: 0,
        suppressOverLimit: true
      });
    }

    const result = await Promise.all([
      this.submitForAvailable(sourcePlan, {
        amount: sourceAmount,
        intent: OperationIntent.Minus
      }),
      this.budgetOperationService.submit(budget, {
        amount: targetAmount,
        intent: OperationIntent.Plus,
        note: fromTitle
      })
    ]);

    return { sourcePlan: result[0], targetBudget: result[1] };
  }

  returnToBudget(plan: Plan, amount: number): Promise<Plan> {
    return this.submitForBoth(plan, {
      amount,
      intent: OperationIntent.Minus,
      note: 'Return to Budget'
    });
  }

  async transferToPlan({
    sourcePlan,
    amount,
    targetPlan,
    targetToCreate
  }: TransferToPlan): Promise<{ sourcePlan: Plan; targetPlan: Plan }> {

    if (!targetPlan) {

      if (!targetToCreate) {
        throw new Error('Target to create is not provided');
      }

      targetPlan = await this.planService.createAsync({
        name: targetToCreate.name,
        availableAmount: 0,
        plannedAmount: 0,
        repeat: false
      });
    }

    const result = await Promise.all([
      this.submitForBoth(targetPlan, {
        amount,
        intent: OperationIntent.Plus,
        note: `Transfer from ${sourcePlan.name}`
      }),
      this.submitForBoth(sourcePlan, {
        amount,
        intent: OperationIntent.Minus,
        note: `Transfer to ${targetPlan.name}`
      })
    ]);

    return { targetPlan: result[0], sourcePlan: result[1] };
  }
}